import {
  shouldReuseProjectWorkspace,
  type RelayCreateSessionOptions,
} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {resolveSessionScope} from '@github-ui/ahp-relay/session-scope-prompt'
import {isCloudSessionStoreEnabled} from '@github-ui/copilot-shared-ui/utils/cloud-session-store-flag'
import {isImageGenerationEnabled} from '@github-ui/copilot-shared-ui/utils/image-generation-flag'

import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {repoCloneUrl} from '../../utils/repo-clone-url'

// Stable identity so the timeline's relay-acquire effect deps don't churn.
const BARE_CREATE_SESSION: RelayCreateSessionOptions = {attachOrCreate: true}
const BARE_CREATE_SESSION_WITH_STORE: RelayCreateSessionOptions = {
  attachOrCreate: true,
  enableSessionStore: true,
}
const BARE_SANDBOX_CREATE_SESSION: RelayCreateSessionOptions = {
  attachOrCreate: true,
  managedPlaywrightMcp: true,
}
const BARE_SANDBOX_CREATE_SESSION_WITH_STORE: RelayCreateSessionOptions = {
  attachOrCreate: true,
  enableSessionStore: true,
  managedPlaywrightMcp: true,
}

const IMAGE_VARIANT_CACHE = new WeakMap<RelayCreateSessionOptions, RelayCreateSessionOptions>()

function bareCreateSessionBase(
  isSandbox: boolean,
  enableSessionStore: boolean,
  imageGeneration: boolean,
): RelayCreateSessionOptions {
  const base = isSandbox
    ? enableSessionStore
      ? BARE_SANDBOX_CREATE_SESSION_WITH_STORE
      : BARE_SANDBOX_CREATE_SESSION
    : enableSessionStore
      ? BARE_CREATE_SESSION_WITH_STORE
      : BARE_CREATE_SESSION
  if (!imageGeneration) return base
  let variant = IMAGE_VARIANT_CACHE.get(base)
  if (!variant) {
    variant = {...base, imageGeneration: true}
    IMAGE_VARIANT_CACHE.set(base, variant)
  }
  return variant
}

export function isUserManagedAhpKind(kind: EnvironmentKind | undefined): boolean {
  return kind === 'user-local' || kind === 'user-codespace'
}

export interface AhpRecoveryCreateSessionInput {
  environmentKind: EnvironmentKind | undefined
  nwo: string | undefined
  cloneUrl?: string
  baseBranch: string | undefined
  /** Owner login a repository-less scoped session is bound to. */
  scopeLogin?: string
  /** Chat runtime mode selected at launch, sent as `createSession.config.mode`. */
  mode?: string
  /** Tool-approval mode selected at launch, sent as `createSession.config.approvalMode`. */
  approvalMode?: SessionApprovalMode
}

/**
 * Task-view `createSession` options that let a hard-navigated environment task
 * recover the daemon session the create page never landed: subscribe first,
 * create only on `-32008`. User-managed AHP and managed sandbox re-drive
 * repo project provisioning when a repository is known, or attach bare otherwise.
 * Returns `undefined` for kinds with no client-driven recovery
 * (managed-cca is MC-driven; managed-actions has no relay session).
 */
export function buildAhpRecoveryCreateSession({
  environmentKind,
  nwo,
  cloneUrl,
  baseBranch,
  scopeLogin,
  mode,
  approvalMode,
}: AhpRecoveryCreateSessionInput): RelayCreateSessionOptions | undefined {
  const isSandbox = environmentKind === 'managed-sandbox'
  if (!isSandbox && !isUserManagedAhpKind(environmentKind)) return undefined
  const enableSessionStore = isCloudSessionStoreEnabled()
  const sessionScope = resolveSessionScope(nwo, scopeLogin)
  const imageGeneration = isImageGenerationEnabled()
  const bareBase = bareCreateSessionBase(isSandbox, enableSessionStore, imageGeneration)
  // The shared constants keep the unscoped shape referentially stable for the
  // timeline's relay-acquire effect deps; a scoped recovery is resolved once
  // per attempt, so a fresh object there costs nothing.
  const scopedCreateSession = sessionScope ? {...bareBase, sessionScope} : bareBase
  const modedCreateSession = mode ? {...scopedCreateSession, mode} : scopedCreateSession
  const bareCreateSession = approvalMode ? {...modedCreateSession, approvalMode} : modedCreateSession
  if (!nwo) return bareCreateSession
  return {
    attachOrCreate: true,
    ...(mode ? {mode} : {}),
    ...(approvalMode ? {approvalMode} : {}),
    enableSessionStore: enableSessionStore || undefined,
    ...(imageGeneration ? {imageGeneration: true} : {}),
    ...(sessionScope ? {sessionScope} : {}),
    ...(isSandbox ? {managedPlaywrightMcp: true} : {}),
    project: {
      nwo,
      cloneUrl: repoCloneUrl(nwo, cloneUrl),
      ...(shouldReuseProjectWorkspace(isSandbox) ? {target: 'workspace'} : {}),
      baseBranch,
    },
  }
}
